import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { PolicyTemplate } from '@/lib/template-utils';

interface UsePolicyTemplatesFilters {
  templateType?: 'global' | 'internal';
  category?: string;
  complianceFramework?: string;
  searchQuery?: string;
  tags?: string[];
}

export function usePolicyTemplates(filters: UsePolicyTemplatesFilters = {}) {
  const [templates, setTemplates] = useState<PolicyTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    fetchTemplates();
  }, [JSON.stringify(filters)]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('policy_templates')
        .select('*')
        .eq('is_active', true);

      // Apply filters
      if (filters.templateType) {
        query = query.eq('template_type', filters.templateType);
      }

      if (filters.category) {
        query = query.eq('category', filters.category);
      }

      if (filters.complianceFramework) {
        query = query.eq('compliance_framework', filters.complianceFramework);
      }

      if (filters.searchQuery) {
        query = query.or(
          `title.ilike.%${filters.searchQuery}%,description.ilike.%${filters.searchQuery}%`
        );
      }

      if (filters.tags && filters.tags.length > 0) {
        query = query.contains('tags', filters.tags);
      }

      const { data, error: queryError } = await query.order('created_at', {
        ascending: false,
      });

      if (queryError) throw queryError;

      setTemplates((data as PolicyTemplate[]) || []);
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError(err as Error);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  return { templates, loading, error, refetch: fetchTemplates };
}

